/**
 * Input-related components: `MuiAutocomplete`, `MuiInput`, `MuiInputBase`, `MuiInputLabel`, `MuiFilledInput`,
 * `MuiOutlinedInput`, `MuiInputAdornment`, `MuiSelect`, `MuiTextField` — filled style, borders, adornments, selects.
 * Discoverability: `MuiTextField`, `TextField`, `Autocomplete`, `FilledInput`, `OutlinedInput`, `Select`.
 *
 * @see https://mui.com/material-ui/api/text-field/
 * @see https://mui.com/material-ui/api/autocomplete/
 *
 */
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import type { Components, Theme } from '@mui/material/styles';
import * as React from 'react';

export function muiInputs(
  theme: Theme,
): Pick<
  Components<Theme>,
  | 'MuiInput'
  | 'MuiInputBase'
  | 'MuiInputLabel'
  | 'MuiFilledInput'
  | 'MuiOutlinedInput'
  | 'MuiInputAdornment'
  | 'MuiTextField'
  | 'MuiSelect'
  | 'MuiAutocomplete'
> {
  const { palette } = theme;
  return {
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ArrowDropDownRoundedIcon />,
      },
      styleOverrides: {
        endAdornment: {
          marginRight: 6,
        },
        listbox: {
          padding: 8,
        },
        option: {
          borderRadius: 8,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          letterSpacing: '0.01071em',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        readOnly: {
          '&$readOnly': {
            background: 'none',
            border: 'none',
          },
        },
      },
      defaultProps: {
        inputProps: {
          autoCorrect: 'off',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          display: 'none',
        },
        outlined: {
          '&$shrink': {
            backgroundColor: theme.palette.mode !== 'dark' ? '#ffffff' : theme.palette.background.default,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: theme.palette.background.default,
          border: `1px solid ${palette.border.light}`,
          // alignItems: 'baseline', // it breaks input adornments
          '&.Mui-focused': {
            background: theme.palette.background.paper,
            border: `1px solid ${palette.primary.main}`,
            boxShadow: `0px 0px 0px 4px ${palette.primary.light}`,
          },
          '&:hover': {
            background: theme.palette.background.paper,
            border: `1px solid ${palette.primary.main}`,
          },
          '&:before, &:after, &:hover:not(.Mui-disabled):before, &:hover:not(.Mui-disabled):after': {
            borderBottom: 0,
          },
          '&.Mui-disabled': {
            background: theme.palette.background.default,
            borderColor: theme.palette.border.light,
            color: theme.palette.text.disabled,
          },
          '&.Mui-error': {
            background: theme.palette.background.default,
            border: `1px solid ${palette.error.main}`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&:hover fieldset': {
            borderColor: theme.palette.action.hover,
          },
        },
        notchedOutline: {
          borderColor: theme.palette.border.main,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: theme.palette.icon.main,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        // variant: 'filled',
        IconComponent: ArrowDropDownRoundedIcon,
      },
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
        icon: {
          color: theme.palette.icon.main,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
        inputProps: {
          'data-1p-ignore': 'true',
        },
      },
      styleOverrides: {
        root: {
          '&.subvariant-plain': {
            verticalAlign: 'middle',
            '& .MuiAutocomplete-endAdornment': {
              marginRight: '0px',
            },
            '& .MuiAutocomplete-inputRoot:before, .MuiAutocomplete-inputRoot:after': {
              borderBottom: 'none !important',
            },
          },
        },
      },
    },
  };
}
